import React from 'react';
import { Chip } from '@mui/material';
import { Text, Theme, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { helpers } from '@simplybenefits/sb-utils';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { 
    Add as AddIcon, 
    Remove as RemoveIcon 
} from '@mui/icons-material';
import CoverageBlock from '../CoverageBlock.js';
import CoverageBlockMedical from '../CoverageBlockMedical.js';
import CoverageBlockMedicalNoMax from '../CoverageBlockMedicalNoMax.js';

const privateHospitalTypeMap = {
    0: i18n.t(tokens.dropdowns.hospitalBenefitsTypes.semiPrivate),
    1: i18n.t(tokens.dropdowns.hospitalBenefitsTypes.private),
    2: i18n.t(tokens.dropdowns.hospitalBenefitsTypes.none)
};

/**
 *
 * @param {*} props
 */
export default function MajorMedical({ classes, expanded, majorMedical, handleChange, defineDeductibleVerbiage, showDependentWarning, waivedEHC }) {
    const privateHospitalType = privateHospitalTypeMap[majorMedical.privateHospital];
    const displayPrivateHospitalBenefit = majorMedical.privateHospital !== 2;

    return (
        <div style={ classes.block }>
            <Accordion
                square
                onChange={handleChange('majorMedical')}
                style={{ boxShadow: 'none' }}
                disableGutters={true}
            >
                <AccordionSummary
                    classes={{
                        content: classes.panelContent,
                        expanded: classes.expanded,
                        root: classes.root
                    }}
                    expandIcon={expanded.includes('majorMedical') ? <RemoveIcon /> : <AddIcon />}
                    sx={classes.accordionSummary}
                >
                    <Text medium bold>Major Medical</Text>
                    {
                        waivedEHC &&
                            <Chip
                                size="small"
                                label="Waived"
                                color="primary"
                                variant="outlined"
                                style={{
                                    margin: "0 10px",
                                    borderRadius: 30,
                                    borderWidth: 2,
                                    borderColor: Theme.colors.lightGrey,
                                    fontFamily: Theme.fonts.type.heading,
                                    fontSize: 14,
                                    color: '#666'
                                }}
                            />
                    }
                </AccordionSummary>
                <AccordionDetails style={classes.panelDetails}>
                    {
                        majorMedical.isEligible !== 1 ?
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    You are currently ineligible for this benefit, if this is a mistake please contact your administrator.
                                </Text>
                            </div>
                            : null
                    }

                    {
                        showDependentWarning &&
                            <div style={classes.row}>
                                <Text small bold margin="20px 0px 10px 0px">
                                    You have waived this benefit for your dependents.
                                </Text>
                            </div>
                    }

                    <div style={classes.row}>
                        <Text small bold margin="10px 0px 10px 0px">
                            {majorMedical.reimbursement > 0 ? `You have ${ majorMedical.reimbursement }% coverage up to the below maximums per listed medical service${ majorMedical.isCombinedMaximum ? ` to a combined maximum of $${ helpers.formatNumber(majorMedical.combinedMaximum) }` : `` }.` : null }
                        </Text>
                    </div>

                    {
                        majorMedical.hasDeductible ?
                            defineDeductibleVerbiage(majorMedical)
                            : null
                    }

                    <React.Fragment>
                        <CoverageBlock
                            title="Benefit"
                            value="Maximum"
                            type="header"
                        />
                        <CoverageBlockMedical
                            coverage={majorMedical}
                        />
                    </React.Fragment>

                    <React.Fragment>
                        <CoverageBlock
                            title="Benefit"
                            value="Reasonable and Customary"
                            type="header"
                        />
                        <CoverageBlockMedicalNoMax
                            coverage={majorMedical}
                        />
                    </React.Fragment>

                    {
                        majorMedical.hearingAids !== 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    {`Hearing Aids: Up to $${ helpers.formatNumber(majorMedical.hearingAids) } per ${ majorMedical.hearingAidsPeriod } months, per insured`}
                                </Text>
                            </div>
                            : null
                    }

                    {
                        majorMedical.includeConvalescentHospital !== 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    {`Convalescent Hospital: ${ majorMedical.convalescentHospitalReimbursement }% reimbursement up to $${ helpers.formatNumber(majorMedical.convalescentHospitalDailyMax) } per day for a maximum of ${ majorMedical.convalescentHospitalMaxDays } days, per insured`}
                                </Text>
                            </div>
                            : null
                    }

                    {
                        majorMedical.cataractSurgeryEyewear !== 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    {`Glasses and Contacts following cataract surgery: $${ helpers.formatNumber(majorMedical.cataractSurgeryEyewear) } per period of ${ majorMedical.cataractSurgeryEyewearPeriod } consecutive months, per insured`}
                                </Text>
                            </div>
                            : null
                    }

                    {
                        majorMedical.apneaMachine > 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    {`Apnea Machine: $${ helpers.formatNumber(majorMedical.apneaMachine) }; 1 per 60 consecutive months`}
                                </Text>
                            </div>
                            : null
                    }

                    {
                        majorMedical.insulinPump > 0 ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    {`Insulin Pump: $${ helpers.formatNumber(majorMedical.insulinPump) } per 5 calendar years`}
                                </Text>
                            </div>
                            : null
                    }

                    <div style={classes.row}>
                        <Text small margin="10px 0px 10px 0px">
                            {`External Breast Prosthesis: 1 per calendar year`}
                        </Text>
                    </div>

                    { displayPrivateHospitalBenefit && (
                        <div style={classes.row}>
                            <Text small margin="10px 0px 10px 0px">
                                { i18n.t(tokens.labels.coverage.hospitalConfinement, { privateHospitalType }) }
                            </Text>
                        </div>
                    )}

                    {
                        majorMedical.includeLaserEyeSurgery ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    {`Laser Eye Surgery: ${ helpers.formatNumber(majorMedical.laserEyeSurgeryReimbursement, 2) }% reimbursement, ${ helpers.formatNumber(majorMedical.laserEyeSurgeryMaximum, 2, true) } per lifetime`}
                                </Text>
                            </div>
                            : 
                            null
                    }

                    {
                        majorMedical.includeGenderAffirmation ?
                            <div style={classes.row}>
                                <Text small margin="10px 0px 10px 0px">
                                    {`Gender Affirmation: ${ helpers.formatNumber(majorMedical.genderAffirmationReimbursement, 2) }% reimbursement, ${ helpers.formatNumber(majorMedical.genderAffirmationMax, 2, true) } per lifetime`}
                                </Text>
                            </div>
                            : 
                            null
                    }

                    <Text small bold margin="10px 0px 0px 0px">
                        Major Medical claims are subject to Reasonable and Customary limitations. Claim reimbursements may be subject to deductions based on your plan design. If you have any questions, please contact your plan administrator.
                    </Text>
                </AccordionDetails>
            </Accordion>
        </div>
    );
}